import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../../../Context'
import { AuthContextApi } from '../../../Context/api'
import { jsonConfig } from '../../../Config'
import { toast } from 'react-toastify'
import {
    InformationCircleIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowsUpDownIcon,
    PlusIcon,
    TagIcon,
    SparklesIcon,
    StarIcon,
    XMarkIcon,
    CheckIcon,
    ClockIcon,
    PencilIcon,
    TrashIcon,
    ChevronLeftIcon

} from '@heroicons/react/24/outline'

const Marca = () => {

    const { states, setters } = useContext(AuthContext)
    const { getMarcas, postMarcas, putMarcas, getEstatisticasMarcas, getMarcaHistorico } = useContext(AuthContextApi)
    const navigate = useNavigate()

    // Estados locais para o formulário
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(jsonConfig.limitDefault)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [selectedMarca, setSelectedMarca] = useState(null)
    const [marcaNome, setMarcaNome] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Estados para histórico de marca
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false)
    const [historicoPage, setHistoricoPage] = useState(0)
    const [historicoHasMore, setHistoricoHasMore] = useState(true)
    const [historicoLoading, setHistoricoLoading] = useState(false)
    const [currentMarcaId, setCurrentMarcaId] = useState(null)

    // Opções para os campos de filtro
    const filterOptions = [
        { value: null, label: 'Selecione um filtro' },
        { value: 'nome', label: 'Nome' },
    ]

    // Opções para ordenação
    const sortOptions = [
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'nome', label: 'Nome' },
    ]

    // Opções para registros por página
    const recordsPerPageOptions = [
        { value: 10, label: '10 registros' },
        { value: 15, label: '15 registros' },
        { value: 25, label: '25 registros' },
        { value: 50, label: '50 registros' },
        { value: 100, label: '100 registros' }
    ]

    // Função para carregar dados
    const loadMarcas = async () => {
        const urlParams = new URLSearchParams()
        urlParams.set('page', states.page)
        urlParams.set('per_page', recordsPerPage)
        urlParams.set('sort', selectedSortField)
        urlParams.set('sort_dir', sortDirection)
        if (selectedFilterField && searchTerm) {
            urlParams.set('filter', selectedFilterField)
            urlParams.set('search', searchTerm)
        }

        console.log('loadMarcas - URL params:', urlParams.toString())
        const result = await getMarcas(urlParams.toString())
        console.log('loadMarcas - Resultado:', result)
        console.log('loadMarcas - Dados carregados:', states.dataMarcas)
    }

    // Função para realizar a busca
    const handleSearch = async () => {
        setters.setPage(0)
        await loadMarcas()
    }

    // Função para limpar o formulário
    const handleClear = async () => {
        setSearchTerm('')
        setSelectedFilterField('nome')
        setSelectedSortField('data_criacao')
        setSortDirection('desc')
        setRecordsPerPage(jsonConfig.limitDefault)
        setters.setPage(0)
        setters.setPer_page(jsonConfig.limitDefault)
        setters.setSort('data_criacao')
        setters.setSort_dir('desc')
        setters.setFilter(null)
        await loadMarcas()
    }

    // Função para lidar com Enter no campo de busca
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    // Função para navegação
    const handleNext = async () => {
        setters.setPage(states.page + 1)
    }

    const handlePrevious = async () => {
        if (states.page > 0) {
            setters.setPage(states.page - 1)
        }
    }

    // Função para abrir modal de cadastro/edição
    const openModal = (marca = null) => {
        console.log('openModal - Marca recebida:', marca)

        setIsEditMode(!!marca)
        setSelectedMarca(marca)
        setMarcaNome(marca?.nome || '')
        setIsModalOpen(true)
    }

    // Função para fechar modal
    const closeModal = () => {
        setIsModalOpen(false)
        setSelectedMarca(null)
        setIsEditMode(false)
        setMarcaNome('')
        setIsSubmitting(false)
    }

    // Função para salvar marca
    const handleSaveMarca = async () => {
        const nomeTrimmed = marcaNome.trim()

        if (!nomeTrimmed) {
            toast.error('O nome da marca é obrigatório')
            return
        }

        if (nomeTrimmed.length > 100) {
            toast.error('O nome da marca deve ter no máximo 100 caracteres')
            return
        }

        if (isEditMode && !selectedMarca?.id) {
            toast.error('ID da marca não encontrado')
            return
        }

        setIsSubmitting(true)

        try {
            let success = false

            if (isEditMode) {
                // Para edição, passamos o ID da marca selecionada
                const editData = {
                    nome: nomeTrimmed,
                    id: selectedMarca.id
                }
                if (!selectedMarca?.id) {
                    toast.error('ID da marca não encontrado')
                    return
                }

                success = await putMarcas(editData)
            } else {
                const createData = {
                    nome: nomeTrimmed
                }
                console.log('Criando marca:', createData)
                success = await postMarcas(createData)
            }

            if (success) {
                closeModal()
                await loadMarcas()
            }
        } catch (error) {
            console.error('Erro ao salvar marca:', error)
            toast.error('Erro ao salvar marca. Tente novamente.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Função para obter ícone baseado no tipo de marca
    const getMarcaIcon = (tipo) => {
        switch (tipo?.toLowerCase()) {
            case 'premium':
            case 'luxo':
                return <StarIcon className="w-6 h-6 text-yellow-500" />
            case 'esportiva':
                return <SparklesIcon className="w-6 h-6 text-purple-500" />
            case 'tradicional':
                return <TagIcon className="w-6 h-6 text-blue-500" />
            default:
                return <TagIcon className="w-6 h-6 text-gray-500" />
        }
    }


    // Função para formatar data
    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    // Função para abrir histórico de marca
    const openHistoricoMarca = async (marcaId) => {
        setCurrentMarcaId(marcaId)
        setHistoricoPage(0)
        setHistoricoHasMore(true)
        await loadHistoricoPage(marcaId, 0, true)
        setIsHistoricoOpen(true)
    }

    // Função para carregar página do histórico
    const loadHistoricoPage = async (marcaId, page, reset = false) => {
        if (!historicoLoading) {
            setHistoricoLoading(true)
            try {
                const query = new URLSearchParams({
                    page: page,
                    per_page: 10
                }).toString()

                await getMarcaHistorico(marcaId, query)

                // Verifica se há mais dados baseado no tamanho da resposta
                const currentData = states.marcaHistorico || []
                setHistoricoPage(page)
            } catch (error) {
                console.error('Erro ao carregar histórico da marca:', error)
                toast.error('Erro ao carregar histórico da marca')
            } finally {
                setHistoricoLoading(false)
            }
        }
    }

    // Função para carregar mais histórico
    const loadMoreHistorico = () => {
        if (historicoHasMore && !historicoLoading && currentMarcaId) {
            loadHistoricoPage(currentMarcaId, historicoPage + 1, false)
        }
    }

    // Função para fechar modal de histórico
    const closeHistoricoModal = () => {
        setIsHistoricoOpen(false)
        setCurrentMarcaId(null)
        setHistoricoPage(0)
        setHistoricoHasMore(true)
        setHistoricoLoading(false)
    }

    // useEffect para monitorar mudanças nas variáveis de query
    useEffect(() => {
        loadMarcas()
    }, [states.page, recordsPerPage, selectedSortField, sortDirection])

    // useEffect para carregar estatísticas reais
    useEffect(() => {
        getEstatisticasMarcas()
    }, [])

    // useEffect para limpeza ao desmontar
    useEffect(() => {
        return () => {
            setters.setPage(0)
            setters.setPer_page(jsonConfig.limitDefault)
            setters.setSort("data_criacao")
            setters.setSort_dir("desc")
            setters.setFilter(null)
        }
    }, [])

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
                {/* Header - Design Artístico */}
                <div className="relative bg-gradient-to-br from-purple-50/80 via-white/80 to-pink-50/80 backdrop-blur-sm border-b border-gradient-to-r from-purple-200/50 to-pink-200/50 sticky top-0 z-10">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full -translate-y-16 translate-x-16"></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="mr-4 p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
                                >
                                    <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
                                </button>
                                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-purple-800 to-pink-800 bg-clip-text text-transparent">
                                    Marcas
                                </h1>
                                <p className="mt-3 text-lg text-gray-600 font-medium">
                                    Gerencie todas as marcas do sistema
                                </p>
                            </div>
                            <div className="mt-6 sm:mt-0">
                                <button
                                    onClick={() => openModal()}
                                    className="group relative inline-flex items-center px-8 py-4 border border-transparent rounded-2xl shadow-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-600 via-purple-700 to-pink-700 hover:from-purple-700 hover:via-purple-800 hover:to-pink-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-300 transform hover:scale-105"
                                >
                                    <div className="relative z-10 flex items-center">
                                        <PlusIcon className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" />
                                        <span className="text-base font-bold">Nova Marca</span>
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Cards de Estatísticas - Design Artístico */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
                        {states.dataEstatisticaMarcas && states.dataEstatisticaMarcas.length > 0 && states.dataEstatisticaMarcas.map((card, index) => (
                            <div key={card.title} className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50/30 backdrop-blur-sm rounded-3xl shadow-lg border border-purple-100/50 hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
                                {/* Background Pattern */}
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-3xl"></div>
                                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full -translate-y-10 translate-x-10"></div>

                                <div className="relative p-6">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-all duration-300">
                                                <InformationCircleIcon className="w-7 h-7 text-white" />
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <dt className="text-sm font-semibold text-gray-700 truncate">
                                                {card.title}
                                            </dt>
                                            <dd className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-purple-800 to-pink-800 bg-clip-text text-transparent">
                                                {card.value}
                                            </dd>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Formulário de Busca - Design Artístico */}
                    <div className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50/30 backdrop-blur-sm rounded-3xl shadow-lg border border-purple-100/50 p-8 mb-8 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-3xl"></div>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-purple-300/10 to-pink-300/10 rounded-full translate-y-12 -translate-x-12"></div>

                        <div className="relative flex items-center mb-8">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg mr-4">
                                <MagnifyingGlassIcon className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-purple-800 to-pink-800 bg-clip-text text-transparent">Pesquisa Avançada</h2>
                        </div>

                        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                            {/* Campo de busca */}
                            <div className="space-y-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Termo de Busca
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Digite sua busca..."
                                        className="w-full px-4 py-3 border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                </div>
                            </div>

                            {/* Campo de filtro */}
                            <div className="space-y-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Campo para Filtrar
                                </label>
                                <div className="relative">
                                    <FunnelIcon className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 z-10" />
                                    <select
                                        value={selectedFilterField}
                                        onChange={(e) => setSelectedFilterField(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white/80 backdrop-blur-sm transition-all duration-300"
                                    >
                                        {filterOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                </div>
                            </div>

                            {/* Campo de ordenação */}
                            <div className="space-y-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Ordenar por
                                </label>
                                <div className="relative">
                                    <ArrowsUpDownIcon className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 z-10" />
                                    <select
                                        value={selectedSortField}
                                        onChange={(e) => setSelectedSortField(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white/80 backdrop-blur-sm transition-all duration-300"
                                    >
                                        {sortOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                </div>
                            </div>

                            {/* Direção da ordenação */}
                            <div className="space-y-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Direção
                                </label>
                                <div className="relative">
                                    <select
                                        value={sortDirection}
                                        onChange={(e) => setSortDirection(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                    >
                                        <option value="asc">Crescente (A-Z)</option>
                                        <option value="desc">Decrescente (Z-A)</option>
                                    </select>
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                </div>
                            </div>

                            {/* Registros por página */}
                            <div className="space-y-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Registros por Página
                                </label>
                                <div className="relative">
                                    <select
                                        value={recordsPerPage}
                                        onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                    >
                                        {recordsPerPageOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                </div>
                            </div>
                        </div>

                        {/* Botões de ação */}
                        <div className="relative flex flex-col sm:flex-row gap-4 mt-8">
                            <button
                                onClick={handleSearch}
                                className="group relative flex-1 sm:flex-none inline-flex justify-center items-center px-8 py-4 border border-transparent text-sm font-semibold rounded-2xl text-white bg-gradient-to-r from-purple-600 via-purple-700 to-pink-700 hover:from-purple-700 hover:via-purple-800 hover:to-pink-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                                <div className="relative z-10 flex items-center">
                                    <MagnifyingGlassIcon className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200" />
                                    <span>Pesquisar</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                            <button
                                onClick={handleClear}
                                className="group relative flex-1 sm:flex-none inline-flex justify-center items-center px-8 py-4 border border-gray-200 text-sm font-semibold rounded-2xl text-gray-700 bg-white/80 hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-300 shadow-sm hover:shadow-md"
                            >
                                <div className="relative z-10 flex items-center">
                                    <span>Limpar Filtros</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>
                    </div>

                    {/* Lista de Marcas - Design Artístico */}
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Lista de Marcas</h2>
                        </div>

                        {states.dataMarcas && states.dataMarcas.length > 0 ? (
                            <>
                                {/* Cabeçalho da tabela - Desktop */}
                                <div className="hidden md:block px-6 py-3 bg-gray-50 border-b border-gray-200">
                                    <div className="grid grid-cols-6 gap-4 items-center text-sm font-medium text-gray-700">
                                        <div className="flex items-center justify-center space-x-2">
                                            <span>Ações</span>
                                        </div>
                                        <div className="flex items-center justify-center space-x-2">
                                            <TagIcon className="w-4 h-4" />
                                            <span>Nome</span>
                                        </div>
                                        <div className="flex items-center justify-center space-x-2">
                                            <ClockIcon className="w-4 h-4" />
                                            <span>Criado em</span>
                                        </div>
                                        <div className="flex items-center justify-center space-x-2">
                                            <ClockIcon className="w-4 h-4" />
                                            <span>Atualizado em</span>
                                        </div>
                                        <div className="flex items-center justify-center space-x-2">
                                            <InformationCircleIcon className="w-4 h-4" />
                                            <span>ID</span>
                                        </div>
                                        <div className="flex items-center justify-center space-x-2">
                                            <StarIcon className="w-4 h-4" />
                                            <span>Status</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Lista de marcas */}
                                <div className="divide-y divide-gray-200">
                                    {states.dataMarcas.map((marca) => (
                                        <div key={marca.id}>
                                            {/* Versão Desktop */}
                                            <div className="hidden md:block px-6 py-4 hover:bg-gray-50 transition-colors duration-200">
                                                <div className="grid grid-cols-6 gap-4 items-center">
                                                    {/* Botões de ação */}
                                                    <div className="flex items-center justify-center space-x-2">
                                                        <button
                                                            onClick={() => openModal(marca)}
                                                            className="p-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:shadow-md transition-all duration-300 transform hover:scale-105"
                                                            title="Editar Marca"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                openHistoricoMarca(marca.id)
                                                            }}
                                                            className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:shadow-md transition-all duration-300 transform hover:scale-105"
                                                            title="Ver Histórico"
                                                        >
                                                            <ClockIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {/* Nome da marca */}
                                                    <div className="text-start">
                                                        <div className="flex items-center justify-start space-x-2">
                                                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-sm">
                                                                {getMarcaIcon(marca.tipo)}
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-900 text-start">
                                                                {marca.nome}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Data de criação */}
                                                    <div className="text-center">
                                                        <span className="text-sm text-gray-700 text-center">
                                                            {formatDate(marca.data_criacao)}
                                                        </span>
                                                    </div>

                                                    {/* Data de atualização */}
                                                    <div className="text-center">
                                                        <span className="text-sm text-gray-700 text-center">
                                                            {formatDate(marca.data_atualizacao)}
                                                        </span>
                                                    </div>

                                                    {/* ID */}
                                                    <div className="text-center">
                                                        <span className="text-sm text-gray-500 font-mono text-center">
                                                            {marca.id?.slice(0, 8)}...
                                                        </span>
                                                    </div>

                                                    {/* Status */}
                                                    <div className="text-center">
                                                        <div className="flex items-center justify-center">
                                                            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-sm" title="Marca Ativa"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Versão Mobile */}
                                            <div className="md:hidden px-4 py-4 hover:bg-gray-50 transition-colors duration-200">
                                                <div className="space-y-3">
                                                    {/* Cabeçalho do card mobile */}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-sm">
                                                                {getMarcaIcon(marca.tipo)}
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-900">{marca.nome}</span>
                                                        </div>
                                                        <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-sm" title="Marca Ativa"></div>
                                                    </div>

                                                    {/* Informações principais */}
                                                    <div className="space-y-2">
                                                        <div className="flex items-center space-x-2">
                                                            <ClockIcon className="w-4 h-4 text-gray-500" />
                                                            <span className="text-sm text-gray-700">Criado: {formatDate(marca.data_criacao)}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <ClockIcon className="w-4 h-4 text-gray-500" />
                                                            <span className="text-sm text-gray-700">Atualizado: {formatDate(marca.data_atualizacao)}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <InformationCircleIcon className="w-4 h-4 text-gray-500" />
                                                            <span className="text-sm text-gray-700">ID: {marca.id?.slice(0, 8)}...</span>
                                                        </div>
                                                    </div>

                                                    {/* Botões de ação mobile */}
                                                    <div className="flex items-center justify-center space-x-2 pt-2 border-t border-gray-200">
                                                        <button
                                                            onClick={() => openModal(marca)}
                                                            className="px-3 py-1 text-xs bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:shadow-md transition-all duration-300"
                                                        >
                                                            Editar
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                openHistoricoMarca(marca.id)
                                                            }}
                                                            className="px-3 py-1 text-xs bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:shadow-md transition-all duration-300"
                                                        >
                                                            Histórico
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="px-6 py-8 text-center text-gray-500">
                                Nenhuma marca encontrada
                            </div>
                        )}
                    </div>

                    {/* Paginação */}
                    {states.dataMarcas && states.dataMarcas.length > 0 && (
                        <div className="mt-8 flex items-center justify-between">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={handlePrevious}
                                    disabled={states.page === 0}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    Anterior
                                </button>
                                <button
                                    onClick={handleNext}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200"
                                >
                                    Próximo
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Mostrando <span className="font-medium">{states.page * recordsPerPage + 1}</span> a{' '}
                                        <span className="font-medium">
                                            {Math.min((states.page + 1) * recordsPerPage, states.dataMarcas.length)}
                                        </span>{' '}
                                        de <span className="font-medium">{states.dataMarcas.length}</span> resultados
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px">
                                        <button
                                            onClick={handlePrevious}
                                            disabled={states.page === 0}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-l-xl"
                                        >
                                            Anterior
                                        </button>
                                        <button
                                            onClick={handleNext}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-all duration-200 rounded-r-xl"
                                        >
                                            Próximo
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal de Cadastro/Edição - Design Artístico */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-md overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-0 w-full max-w-md">
                            <div className="relative flex max-h-[90vh] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                                {/* Background Pattern */}
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5"></div>

                                {/* Header do Modal */}
                                <div className="relative z-10 shrink-0 bg-gradient-to-r from-purple-600 via-purple-700 to-pink-700 px-8 py-6">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-2xl font-bold text-white">
                                            {isEditMode ? 'Editar Marca' : 'Nova Marca'}
                                        </h3>
                                        <button
                                            onClick={closeModal}
                                            className="text-white/80 hover:text-white transition-colors transform hover:scale-110 duration-200"
                                        >
                                            <XMarkIcon className="w-7 h-7" />
                                        </button>
                                    </div>
                                </div>

                                {/* Conteúdo do Modal */}
                                <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
                                    <div className="min-h-0 flex-1 overflow-y-auto p-8">
                                    <div className="space-y-8">
                                        {/* Ícone e Descrição */}
                                        <div className="text-center">
                                            <div className="relative w-20 h-20 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                                                <TagIcon className="w-10 h-10 text-white" />
                                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
                                            </div>
                                            <p className="text-base text-gray-700 font-medium">
                                                {isEditMode
                                                    ? 'Atualize o nome da marca selecionada'
                                                    : 'Digite o nome da nova marca'
                                                }
                                            </p>
                                        </div>

                                        {/* Campo de Nome */}
                                        <div className="space-y-3">
                                            <label className="block text-sm font-semibold text-gray-700">
                                                Nome da Marca
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={marcaNome}
                                                    onChange={(e) => setMarcaNome(e.target.value)}
                                                    placeholder="Digite o nome da marca..."
                                                    maxLength={100}
                                                    className="w-full px-6 py-4 border border-gray-200 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 text-lg bg-white/80 backdrop-blur-sm"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !isSubmitting) {
                                                            handleSaveMarca()
                                                        }
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                            </div>
                                            <div className="flex justify-between text-sm text-gray-500 font-medium">
                                                <span>Máximo 100 caracteres</span>
                                                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-bold">{marcaNome.length}/100</span>
                                            </div>
                                        </div>

                                        {/* Informações Adicionais */}
                                        <div className="bg-gradient-to-r from-purple-50/50 to-pink-50/50 rounded-2xl p-6 border border-purple-100/30">
                                            <div className="flex items-center text-sm text-gray-700 font-medium">
                                                <InformationCircleIcon className="w-5 h-5 mr-3 text-purple-500" />
                                                <span>
                                                    {isEditMode
                                                        ? `Marca criada em ${formatDate(selectedMarca?.data_criacao)}`
                                                        : 'A marca será criada com as configurações padrão'
                                                    }
                                                </span>
                                            </div>
                                            {isEditMode && (
                                                <div className="mt-3 text-sm text-gray-600 font-medium bg-white/50 rounded-xl p-3 border border-purple-100/30">
                                                    <span className="font-semibold">ID:</span> {selectedMarca?.id}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    </div>

                                    {/* Botões de Ação */}
                                    <div className="flex shrink-0 gap-4 border-t border-gray-100/80 bg-white px-8 py-6">
                                        <button
                                            onClick={closeModal}
                                            disabled={isSubmitting}
                                            className="group relative flex-1 px-6 py-4 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-700 bg-white/80 hover:bg-white transition-all duration-300 disabled:opacity-50 shadow-sm hover:shadow-md"
                                        >
                                            <div className="relative z-10 flex items-center justify-center">
                                                <span>Cancelar</span>
                                            </div>
                                            <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </button>
                                        <button
                                            onClick={handleSaveMarca}
                                            disabled={!marcaNome.trim() || isSubmitting}
                                            className="group relative flex-1 px-6 py-4 border border-transparent rounded-2xl text-sm font-semibold text-white bg-gradient-to-r from-purple-600 via-purple-700 to-pink-700 hover:from-purple-700 hover:via-purple-800 hover:to-pink-800 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl"
                                        >
                                            <div className="relative z-10 flex items-center justify-center">
                                                {isSubmitting ? (
                                                    <>
                                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                                        <span>Salvando...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckIcon className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200" />
                                                        <span className="font-bold">{isEditMode ? 'Atualizar' : 'Criar'}</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Histórico de Marca */}
                {isHistoricoOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50">
                        <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                            {/* Header do Modal */}
                            <div className="shrink-0 border-b border-gray-100 px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                                            <ClockIcon className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">
                                                Histórico da Marca
                                            </h2>
                                            <p className="text-sm text-gray-500">
                                                Visualize todas as alterações realizadas nesta marca
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeHistoricoModal}
                                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-200"
                                    >
                                        <XMarkIcon className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                            </div>

                            {/* Conteúdo do Histórico */}
                            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                                {states.marcaHistorico && states.marcaHistorico.length > 0 ? (
                                    <div className="space-y-4">
                                        {states.marcaHistorico.map((item, index) => (
                                            <div key={item.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                                {/* Header do Item */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${item.transformacao === 'Criação' ? 'bg-green-100' :
                                                            item.transformacao === 'Edição' ? 'bg-blue-100' :
                                                                'bg-gray-100'
                                                            }`}>
                                                            {item.transformacao === 'Criação' ? (
                                                                <PlusIcon className="w-4 h-4 text-green-600" />
                                                            ) : item.transformacao === 'Edição' ? (
                                                                <PencilIcon className="w-4 h-4 text-blue-600" />
                                                            ) : (
                                                                <TrashIcon className="w-4 h-4 text-gray-600" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-900">
                                                                {item.transformacao}
                                                            </h3>
                                                            <p className="text-xs text-gray-500">
                                                                por {item.user_email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(item.data_atualizacao).toLocaleDateString('pt-BR')}
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            {new Date(item.data_atualizacao).toLocaleTimeString('pt-BR')}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Dados Atuais */}
                                                {item.dado_atual && (
                                                    <div className="mb-3">
                                                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                            Dados Atuais
                                                        </h4>
                                                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                {item.dado_atual.nome && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Nome</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{item.dado_atual.nome}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_atual.data_criacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Criado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_atual.data_criacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_atual.data_atualizacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Atualizado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_atual.data_atualizacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_atual.deletado !== undefined && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Status</p>
                                                                        <p className="text-sm font-semibold text-gray-900">
                                                                            {item.dado_atual.deletado ? 'Deletado' : 'Ativo'}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Dados Anteriores (se houver) */}
                                                {item.dado_antigo && (
                                                    <div>
                                                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                            Dados Anteriores
                                                        </h4>
                                                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                {item.dado_antigo.nome && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Nome</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{item.dado_antigo.nome}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_antigo.data_criacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Criado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_antigo.data_criacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_antigo.data_atualizacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Atualizado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_antigo.data_atualizacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_antigo.deletado !== undefined && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Status</p>
                                                                        <p className="text-sm font-semibold text-gray-900">
                                                                            {item.dado_antigo.deletado ? 'Deletado' : 'Ativo'}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Botão Carregar Mais */}
                                        {historicoHasMore && (
                                            <div className="flex justify-center mt-6">
                                                <button
                                                    onClick={loadMoreHistorico}
                                                    disabled={historicoLoading}
                                                    className="group relative inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 via-purple-700 to-purple-700 hover:from-purple-700 hover:via-purple-800 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {historicoLoading ? (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                            Carregando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <PlusIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                                            Carregar Mais
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum histórico encontrado</h3>
                                        <p className="text-gray-500">Esta marca ainda não possui histórico de alterações.</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer do Modal */}
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                                <button
                                    onClick={closeHistoricoModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default Marca